import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { TableRow as MuiTableRow } from '@mui/material';
import { useState } from 'react';
import { useTheme } from '../../hooks';
import { displayNullable } from '../../types';
import { styled } from '../../utils';
import { IconButton } from '../IconButton';
import { Menu, MenuItem } from '../Menu';
import { ExpandRow } from './ExpandRow';
import { ExpandRowComponent } from './ExpandRowComponent';
import { TableCell } from './TableCell';
export const TableRowStyled = styled(MuiTableRow)(({ theme }) => ({
    borderTop: `1px solid ${theme.palette.groupPrimary.lightBlue}`,
}));
export const CollapseRow = styled(TableRowStyled, {
    shouldForwardProp: (prop) => prop !== 'seperateSubRow',
})(({ theme, seperateSubRow }) => ({
    '& .MuiTableCell-root': {
        padding: 0,
    },
    borderTop: seperateSubRow
        ? `1px solid ${theme.palette.groupPrimary.lightBlue}`
        : 0,
}));
const IconButtonStyled = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shorter,
        easing: theme.transitions.easing.easeOut,
    }),
    transform: open ? 'rotate(180deg)' : 'rotate(0)',
}));
export function getValue(dataRow, column, shouldDisplayNullable) {
    if (column?.formatFunction) {
        const hasValue = column?.value(dataRow) !== null && column?.value(dataRow) !== undefined;
        return shouldDisplayNullable
            ? displayNullable(hasValue && column.formatFunction(dataRow))
            : hasValue && column.formatFunction(dataRow);
    }
    return shouldDisplayNullable
        ? displayNullable(column?.value(dataRow))
        : column?.value(dataRow);
}
export function hasSubRow(dataRow, subTable) {
    return !!subTable && subTable.data(dataRow)?.length > 0;
}
export const getStriped = (stripes, i) => {
    if (stripes === 'odd') {
        return (i + 1) % 2 === 0;
    }
    if (stripes === 'even') {
        return (i + 1) % 2 !== 0;
    }
    return false;
};
function getPropOrFunction(dataRow, prop) {
    return prop instanceof Function ? prop(dataRow) : prop;
}
function getStyles(dataRow, column, theme) {
    if (typeof column?.style === 'function') {
        return column?.style(dataRow, theme);
    }
    return column?.style;
}
export function TableRow({ columns, dataRow, SubComponent, isExpandable, showIndex, dataLength, index, open, menuEntries, subTable, stripes = 'odd', seperateSubRow = false, displayNullable = true, cellPadding = 'default', size = 'default', menuIconVariant, menuIconDisabled = false, alternativeCellAlignmentRight, alignTextCell, }) {
    const theme = useTheme();
    const [expandOpen, setExpandOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);
    const handleMenuClick = (e) => {
        setAnchorEl(e.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    const visibleMenuEntries = menuEntries?.some((entry) => !getPropOrFunction(dataRow, entry.hidden));
    const showMenu = menuEntries && visibleMenuEntries;
    const showCloseButton = showMenu && menuEntries.some((entry) => entry.action === 'expandRow');
    return (_jsxs(_Fragment, { children: [_jsxs(TableRowStyled, { children: [isExpandable && (_jsx(TableCell, { width: "40", className: "expandCell", cellPadding: cellPadding, cellSize: size, align: alignTextCell, children: ((subTable && hasSubRow(dataRow, subTable)) || SubComponent) && (_jsx(IconButtonStyled, { onClick: () => setExpandOpen(!expandOpen), variant: "arrow-down-2", size: "small", color: "inherit", open: expandOpen })) })), showIndex && (_jsx(TableCell, { striped: getStriped(stripes, 1), cellPadding: cellPadding, cellSize: size, align: alignTextCell, children: showIndex === 'asc' ? index + 1 : dataLength - index })), columns.map((column, i) => !column.hide?.(dataRow) && (_jsx(TableCell, { striped: getStriped(stripes, i), cellPadding: cellPadding, cellSize: size, colSpan: column.colSpan, style: getStyles(dataRow, column, theme), align: alternativeCellAlignmentRight && i > 0
                            ? 'right'
                            : i > 0
                                ? alignTextCell
                                : undefined, children: getValue(dataRow, column, displayNullable) }, i))), showMenu && (_jsxs(TableCell, { striped: getStriped(stripes, columns.length), cellPadding: cellPadding, cellSize: size, align: alignTextCell ?? 'center', children: [_jsx(IconButton, { variant: menuIconVariant ? menuIconVariant : 'more-vertical-filled', onClick: (e) => handleMenuClick(e), color: "inherit", disabled: menuIconDisabled }), _jsx(Menu, { open: menuOpen, anchorEl: anchorEl, onClose: handleMenuClose, children: menuEntries.map((entry, i) => !getPropOrFunction(dataRow, entry.hidden) && (_jsx(MenuItem, { onClick: () => {
                                        if (entry.action === 'expandRow') {
                                            setExpandOpen(true);
                                        }
                                        else {
                                            entry.action(dataRow, index);
                                        }
                                        handleMenuClose();
                                    }, disabled: getPropOrFunction(dataRow, entry.disabled), children: _jsx(_Fragment, { children: entry.children ? (_jsx(entry.children, { data: dataRow, label: getPropOrFunction(dataRow, entry.label) })) : (getPropOrFunction(dataRow, entry.label)) }) }, i))) })] }))] }), (SubComponent || (subTable && hasSubRow(dataRow, subTable))) &&
                SubComponent ? (_jsx(ExpandRowComponent, { index: index, showIndex: showIndex, dataRow: dataRow, dataLength: dataLength, columns: columns, SubComponent: SubComponent, isExpandable: isExpandable, open: open, seperateSubRow: seperateSubRow, cellPadding: cellPadding, cellSize: size, showCloseButton: !!showCloseButton, expandOpen: expandOpen, setExpandOpen: setExpandOpen })) : (_jsx(ExpandRow, { index: index, showIndex: showIndex, dataRow: dataRow, dataLength: dataLength, columns: columns, isExpandable: isExpandable, open: open, subTable: subTable, seperateSubRow: seperateSubRow, stripes: stripes, displayNullable: displayNullable, cellPadding: cellPadding, cellSize: size, showMenu: !!showMenu, expandOpen: expandOpen, CollapseRow: CollapseRow }))] }));
}
